import type { InspirationReaction } from "@/library/model/wire.js";
import { IconHeart, IconThumbsDown } from "@/platform/ui";
import { cx } from "@/platform/ui";

/**
 * The three-state reaction toggle: like / nothing / dislike.
 *
 * Clicking the state you're already in clears it — that is the "nothing" third
 * state, and there is no separate control for it.
 *
 * Quotes and artworks each get their own of these, deliberately: loving a line
 * of Rilke says nothing about the painting it arrived with, and keeping them
 * apart is what lets the next batch be briefed on "more Hokusai, less
 * Nietzsche". See docs/inspiration.md.
 *
 * Dislike is also this module's soft-delete — a disliked piece is never picked
 * again — which is why it needs to sit here, one click away, and not behind a
 * menu.
 */
export function ReactionButtons({
  reaction,
  onChange,
  label,
  size = 14,
  showDislike = true,
}: {
  reaction: InspirationReaction | null;
  onChange: (next: InspirationReaction | null) => void;
  /** What is being reacted to — for screen readers, e.g. "quote". */
  label: string;
  size?: number;
  /**
   * Gallery tiles pass `false`. Two unlabeled heart/thumb pairs stacked on a
   * small tile are unreadable — you cannot tell which row is the quote's. The
   * tile keeps the hearts (collecting is the common act); dislike lives in the
   * modal, where there is room to label what it applies to.
   */
  showDislike?: boolean;
}) {
  const toggle = (next: InspirationReaction) => onChange(reaction === next ? null : next);

  const btn = (active: boolean, tone: "accent" | "negative") =>
    cx(
      "rounded-md p-1 transition-colors",
      active
        ? tone === "accent"
          ? "text-accent"
          : "text-negative"
        : "text-faint hover:text-ink-soft",
    );

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        aria-label={reaction === "like" ? `Unlike this ${label}` : `Like this ${label}`}
        aria-pressed={reaction === "like"}
        onClick={(e) => {
          e.stopPropagation(); // never opens the card it sits on
          toggle("like");
        }}
        className={btn(reaction === "like", "accent")}
      >
        <IconHeart size={size} filled={reaction === "like"} />
      </button>
      {showDislike && (
        <button
          type="button"
          aria-label={
            reaction === "dislike"
              ? `Undo dislike of this ${label}`
              : `Show me fewer like this ${label}`
          }
          aria-pressed={reaction === "dislike"}
          onClick={(e) => {
            e.stopPropagation();
            toggle("dislike");
          }}
          className={btn(reaction === "dislike", "negative")}
        >
          <IconThumbsDown size={size} filled={reaction === "dislike"} />
        </button>
      )}
    </div>
  );
}
